import { userHasPermission } from './permissions';

const ROLE_PRIORITY = [
  'PLATFORM ADMINISTRATOR',
  'TENDER ADMINISTRATOR',
  'BID APPROVER',
  'BID MANAGER',
  'COMMERCIAL REVIEWER',
  'TECHNICAL REVIEWER',
  'TIMESHEET APPROVER',
  'CONTRIBUTOR',
  'MANAGEMENT VIEWER'
];

const ROLE_LABELS = {
  'PLATFORM ADMINISTRATOR': 'Platform Administrator',
  'TENDER ADMINISTRATOR': 'Tender Administrator',
  'BID MANAGER': 'Bid Manager',
  'TECHNICAL REVIEWER': 'Technical Reviewer',
  'COMMERCIAL REVIEWER': 'Commercial Reviewer',
  'BID APPROVER': 'Bid Approver',
  'TIMESHEET APPROVER': 'Timesheet Approver',
  CONTRIBUTOR: 'Contributor',
  'MANAGEMENT VIEWER': 'Management Viewer'
};

function pickPrimaryRole(roles = []) {
  const set = new Set(roles.map((r) => String(r).trim().toUpperCase()));
  for (const role of ROLE_PRIORITY) {
    if (set.has(role)) return role;
  }
  return roles[0] ? String(roles[0]).trim().toUpperCase() : 'CONTRIBUTOR';
}

export function buildRoleDashboardFallback(bootstrap, summary, user) {
  const roles = user?.roles || bootstrap?.user?.roles || [];
  const primaryRole = pickPrimaryRole(roles);
  const pending = summary?.pendingWork ?? 0;
  const overdue = summary?.overdueWork ?? 0;
  const perms = bootstrap?.permissions || [];

  const priorities = (summary?.recentActivity || []).map((item) => ({
    id: String(item._id),
    title: item.title,
    description: item.description || '',
    type: item.type,
    status: item.status,
    routePath: item.routePath || null
  }));

  const actionsByRole = {
    'PLATFORM ADMINISTRATOR': [
      { label: 'Company setup', route: '/platform/company', variant: 'primary' },
      { label: 'Manage users', route: '/platform/users', variant: 'outline' }
    ],
    'BID MANAGER': [
      { label: 'Tender register', route: '/apps/tender/register', variant: 'primary' },
      { label: 'My work', route: '/my-work', variant: 'outline' }
    ],
    CONTRIBUTOR: [
      { label: 'Timesheets', route: '/apps/tender/timesheets', variant: 'primary' },
      { label: 'My work', route: '/my-work', variant: 'outline' }
    ]
  };

  return {
    primaryRole,
    roleLabel: ROLE_LABELS[primaryRole] || primaryRole,
    subtitle: 'Summary from workspace data — restart the API server to enable the full role dashboard.',
    kpis: [
      {
        key: 'pending',
        label: 'Your open items',
        value: pending,
        hint: overdue ? `${overdue} overdue` : 'Assigned to you',
        tone: overdue > 0 ? 'risk' : pending > 0 ? 'warning' : 'success'
      },
      {
        key: 'apps',
        label: 'Applications',
        value: (bootstrap?.applications || []).filter((a) => a.enabled && a.entitled).length,
        hint: 'Ready to open',
        tone: 'intel'
      }
    ],
    actions: actionsByRole[primaryRole] || actionsByRole.CONTRIBUTOR,
    priorities,
    insights: [
      {
        title: pending > 0 ? `${pending} item(s) in your queue` : 'Your work queue is clear',
        detail: userHasPermission(perms, 'tender.read')
          ? 'Open Tender Management from applications or quick actions.'
          : 'Use quick actions to continue in the workspace.',
        tone: pending > 0 ? 'warning' : 'success'
      }
    ],
    company: {
      displayName: bootstrap?.company?.displayName,
      onboardingStatus: bootstrap?.company?.onboardingStatus
    }
  };
}
